import { useEffect, useState } from "react";
import Bird from "../../assets/images/bird.png";
import Coin from "../../assets/images/coin.png";
import Ton from "../../assets/images/ton.png";
import Donator from "../../assets/images/donator.svg";
import QRCode from "../../assets/images/qrcode.svg";
import { AiFillCaretDown } from "react-icons/ai";
import { FiCopy, FiCheck } from "react-icons/fi";
import LineChart from "./LineChart";
import Button from "../../components/Button";
import Socials from "../../components/Socials";
import Partners from "../../components/Partners";
import { WALLET, APP_URL } from "../../data/site";

const CARDS = [
  {
    n: 1,
    title: "Sustainable Growth",
    text: "Built for long-term holders: a token designed for durable, compounding growth instead of quick gains and volatility.",
  },
  {
    n: 2,
    title: "Auto-Compounding",
    text: "Rewards are reinvested automatically every rebase, so your DREAM balance grows without a single click.",
  },
  {
    n: 3,
    title: "Treasury-Backed",
    text: "A diversified on-chain treasury backs the price floor and funds steady buybacks.",
  },
  {
    n: 4,
    title: "Anti-Dump",
    text: "Our anti-dump policy keeps large sell-offs from disrupting the market or harming holders' portfolios.",
  },
];

const STATS = [
  { title: "TON staked", value: "549,326,121" },
  { title: "~BTC", value: "44,455.57" },
  { title: "Received to DAO", value: "39,120,432" },
  { title: "Received via NFT", value: "410,415,125" },
];

const ROUNDS = [
  { round: 1, nftSold: 100, price: 1 },
  { round: 2, nftSold: 250, price: 1.5 },
  { round: 3, nftSold: 500, price: 2 },
  { round: 4, nftSold: 750, price: 2.5 },
  { round: 5, nftSold: 1000, price: 3 },
  { round: 6, nftSold: 1500, price: 4 },
];

const DONATORS = [
  { name: "Hope Horizon", place: "Tokyo, Japan", amount: "$6,713,604" },
  { name: "Care Hub", place: "London, UK", amount: "$4,218,902" },
  { name: "TON Builders Guild", place: "New York, USA", amount: "$2,940,117" },
];

// Reveal-on-scroll. Content is visible by default; we only "arm" (hide, then
// fade in) sections that start below the fold, so nothing can get stuck hidden.
function useReveal() {
  useEffect(() => {
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduce || !("IntersectionObserver" in window)) return;

    const vh = window.innerHeight;
    const armed = Array.from(document.querySelectorAll(".reveal")).filter(
      (el) => el.getBoundingClientRect().top > vh * 0.85
    );
    armed.forEach((el) => el.classList.add("is-armed"));

    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );
    armed.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function Index() {
  const [copied, setCopied] = useState(false);
  useReveal();

  const copyWallet = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(WALLET).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <div className="home__wrapper">
      <section className="home__landing-section pt-24">
        <div className="container mx-auto">
          <div className="home__hero">
            <p className="home__hero-kicker">
              The best auto-staking &amp; auto-compounding protocol on TON
            </p>

            <h1 className="home__hero-title">Aldebaran</h1>

            <img src={Bird} alt="" className="mx-auto home__image" />

            <p className="home__hero-sub">
              Buy and hold DREAM to earn auto-compounding rewards on TON - paid
              out every rebase, with no staking screens and no lockups.
            </p>

            <div className="home__hero-cta">
              <Button to={APP_URL} className="btn--lg">
                Get started
              </Button>
              <Button href="#statistics" variant="ghost" className="btn--lg">
                View stats
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="py-10">
        <div className="container mx-auto">
          <div className="home__cards reveal">
            {CARDS.map((card) => (
              <div className="home__card" key={card.n}>
                <img
                  src={`/card-icons/${card.n}.svg`}
                  alt=""
                  className="mb-10"
                  width="56"
                  height="56"
                />
                <h2 className="home__card__title text-xl mb-2">{card.title}</h2>
                <p className="home__card__text text-md">{card.text}</p>
              </div>
            ))}
          </div>

          <section className="mt-20 home__blocks reveal">
            <div className="home__blocks__item gap-4">
              <div className="flex flex-col items-start gap-4 p-10">
                <h2 className="text-3xl">Earn a dynamic APY</h2>

                <p className="text-2xl home__accent">Rebased every 30 minutes</p>

                <p className="text-secondary-text">
                  Aldebaran is built as a safe, long-term opportunity for
                  realistic, careful investors - rewards adjust to protocol
                  health rather than chasing a fixed headline number.
                </p>

                <Button href="/documents" variant="ghost">
                  Learn more
                </Button>
              </div>

              <img
                src="/charts/1.png"
                alt="Projected APY trend curve"
                className="w-full"
              />
            </div>

            <div className="home__blocks__item flex flex-col items-start gap-4 p-10">
              <h2 className="text-3xl">Staking &amp; Compounding</h2>

              <p className="text-2xl">Automatic staking in your wallet</p>

              <p className="text-secondary-text">
                Stake straight from your wallet - no bridges, no custodial
                screens. Aldebaran compounds your position for you, around the
                clock.
              </p>

              <Button href="/instructions" variant="ghost">
                Learn more
              </Button>

              <div className="mt-auto flex flex-col gap-4 w-full">
                <div className="home__blocks__item__card">
                  <p className="mb-4">
                    <span className="mr-2">Results</span>
                    <span className="text-secondary-text">1,726 votes</span>
                  </p>

                  <div className="flex items-center justify-between mb-2">
                    <p>
                      Yes <span className="text-secondary-text">91.75%</span>
                    </p>

                    <div className="flex text-secondary-text">
                      1.29K votes · 1.67M TON
                    </div>
                  </div>
                  <div
                    className="home__blocks__item__card__loading"
                    role="progressbar"
                    aria-label="Vote result: 91.75% yes"
                    aria-valuenow="92"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    <div className="home__blocks__item__card__loading__inner"></div>
                  </div>
                </div>

                <div className="home__blocks__item__card">
                  <p className="mb-4">
                    <span className="mr-2">Winner</span>
                    <span className="text-secondary-text">1,543 votes</span>
                  </p>

                  <div className="flex gap-4 items-center">
                    <img src={Coin} alt="" width="48" height="48" />

                    <div className="flex flex-col gap">
                      <p>Stake CAKE</p>
                      <p className="text-secondary-text">
                        Stake, earn - and more!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="home__rewards mt-8 reveal">
            <div className="home__rewards__image">
              <img src={Ton} alt="" />
            </div>

            <div className="py-14 h-full justify-between flex flex-col lg:items-start items-center gap-2 lg:text-left">
              <h2 className="lg:text-5xl text-3xl">Get Rewards</h2>

              <p className="lg:text-3xl text-xl">
                Every 30 minutes / 48 times daily
              </p>

              <p className="text-secondary-text">
                Holding DREAM is all it takes - rewards land in your wallet
                automatically, no claiming and no gas-heavy transactions.
              </p>

              <Button href="/documents" variant="ghost">
                Learn more
              </Button>
            </div>
          </section>
        </div>

        <div className="px-4">
          <section
            id="statistics"
            className="home__stats__wrapper reveal"
            aria-labelledby="stats-title"
          >
            <div className="container">
              <div className="home__stats py-20">
                <div className="flex flex-col gap-3 text-center lg:w-6/12 mx-auto mb-10">
                  <h2
                    id="stats-title"
                    className="uppercase text-3xl md:text-5xl"
                  >
                    Statistics
                  </h2>

                  <p className="text-secondary-text">
                    A live look at the Aldebaran ecosystem's performance, usage,
                    and growth across the TON network.
                  </p>
                </div>

                <div className="flex flex-col gap-8">
                  <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-8">
                    {STATS.map((stat) => (
                      <div
                        className="home__stats__card flex flex-col gap-4 p-8"
                        key={stat.title}
                      >
                        <p className="text-secondary-text">{stat.title}</p>
                        <p className="text-3xl tabular-nums">{stat.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="grid lg:grid-cols-2 md:grid-cols-1 gap-8">
                    <div className="home__stats__card flex flex-col gap-4">
                      <div className="p-8">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                          <h3 className="font-medium">
                            Received from staking/farming profits
                          </h3>

                          <button
                            type="button"
                            className="home__chip"
                            aria-label="Filter: last 28 days"
                          >
                            <span>Last 28 days</span>
                            <AiFillCaretDown aria-hidden="true" />
                          </button>
                        </div>

                        <div className="flex items-end flex-wrap gap-4 mb-4">
                          <p className="text-3xl md:text-5xl font-medium tabular-nums">
                            $2.14B
                          </p>
                          <p className="text-secondary-text">April 7, 2024</p>
                        </div>
                      </div>

                      <LineChart />
                    </div>

                    <div className="home__stats__card flex flex-col gap-4">
                      <div className="p-8">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                          <h3 className="font-medium">
                            Transferred to rewards for holders
                          </h3>

                          <button
                            type="button"
                            className="home__chip"
                            aria-label="Filter: last 28 days"
                          >
                            <span>Last 28 days</span>
                            <AiFillCaretDown aria-hidden="true" />
                          </button>
                        </div>

                        <div className="flex items-end flex-wrap gap-4 mb-4">
                          <p className="text-3xl md:text-5xl font-medium tabular-nums">
                            $315.49B
                          </p>
                          <p className="text-secondary-text">April 7, 2024</p>
                        </div>
                      </div>

                      <img
                        src="/charts/3.png"
                        alt="Rewards transferred to holders over the last 28 days, trending upward"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="home__stats__card p-8">
                    <div className="home__stats__table__wrapper">
                      <div className="flex flex-col gap-12">
                        <div className="flex flex-col">
                          <p className="text-secondary-text text-lg">
                            Jettons sold
                          </p>
                          <p className="text-4xl tabular-nums">10 000 000</p>
                        </div>

                        <div className="flex flex-col gap-4">
                          <p className="text-secondary-text text-lg">
                            Staking jettons
                          </p>
                          <p className="text-4xl tabular-nums">100 000</p>
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                          <Button to={APP_URL}>Enter App</Button>
                          <Button href="/documents" variant="ghost">
                            Read newspaper
                          </Button>
                        </div>
                      </div>

                      <div className="rounded-xl overflow-hidden home__stats__table">
                        <table className="text-center table-auto w-full h-full">
                          <caption className="sr-only">
                            NFT sale rounds
                          </caption>
                          <thead>
                            <tr>
                              <th scope="col">Round</th>
                              <th scope="col">NFT sold</th>
                              <th scope="col">Price, TON</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ROUNDS.map((r) => (
                              <tr key={r.round}>
                                <td>{r.round}</td>
                                <td className="tabular-nums">{r.nftSold}</td>
                                <td className="tabular-nums">{r.price}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col mt-10">
                    <div className="flex flex-col gap-6 text-center mb-16">
                      <h2 className="uppercase text-3xl md:text-4xl">Donate</h2>

                      <p className="text-xl md:text-2xl">
                        Transfer any amount of TON to the Aid Pool, without
                        limitations.
                      </p>

                      <p className="text-secondary-text">
                        The Aid Pool allocates funds to assistance funds across
                        the globe.
                      </p>
                    </div>
                    <div className="home__stats__donate__grid">
                      <div className="flex flex-col gap-8">
                        <h3 className="text-xl mb-8">Statistics</h3>

                        <div className="flex flex-col gap-8">
                          <div className="flex flex-col gap-1">
                            <p className="text-secondary-text">Wallet 1</p>
                            <p className="text-3xl md:text-5xl tabular-nums">
                              $8,302,569
                            </p>
                          </div>

                          <div className="flex flex-col gap-1">
                            <p className="text-secondary-text">Wallet 2</p>
                            <p className="text-3xl md:text-5xl tabular-nums">
                              $12,861,492
                            </p>
                          </div>
                        </div>

                        <div className="flex md:flex-nowrap flex-wrap gap-8">
                          <div className="flex flex-col gap-1">
                            <p className="text-secondary-text">
                              Royalty wallet, 1%
                            </p>
                            <p className="text-2xl tabular-nums">$1,404</p>
                          </div>

                          <div className="flex flex-col gap-1">
                            <p className="text-secondary-text">
                              Donations wallet
                            </p>
                            <p className="text-2xl tabular-nums">$20,140,581</p>
                          </div>
                        </div>
                      </div>

                      <ul className="flex flex-col gap-8">
                        {DONATORS.map((donator) => (
                          <li className="home__stats__card p-4" key={donator.name}>
                            <div className="flex items-center justify-between flex-wrap gap-4">
                              <div className="flex items-center gap-4">
                                <img src={Donator} alt="" width="40" height="40" />

                                <div className="flex flex-col">
                                  <p className="font-medium">{donator.name}</p>
                                  <p className="text-secondary-text">
                                    {donator.place}
                                  </p>
                                </div>
                              </div>

                              <p className="tabular-nums">{donator.amount}</p>
                            </div>
                          </li>
                        ))}
                      </ul>

                      <div className="flex flex-col gap-8">
                        <div className="home__stats__card flex flex-col p-8 px-14 items-center gap-4">
                          <img
                            src={QRCode}
                            alt="QR code for the TON donation wallet"
                            className="h-30"
                          />

                          <button
                            type="button"
                            onClick={copyWallet}
                            className="home__copy"
                            aria-label="Copy donation wallet address"
                          >
                            {copied ? (
                              <FiCheck aria-hidden="true" />
                            ) : (
                              <FiCopy aria-hidden="true" />
                            )}
                            <span>{copied ? "Copied!" : "Copy address"}</span>
                          </button>
                        </div>

                        <div className="home__stats__card p-4 text-center break-all">
                          <p className="text-secondary-text text-sm">{WALLET}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section
          id="roadmap"
          className="home__roadmap reveal"
          aria-labelledby="roadmap-title"
        >
          <div className="container">
            <div className="flex flex-col gap-4 text-center">
              <h2 id="roadmap-title" className="text-3xl md:text-5xl uppercase">
                Roadmap
              </h2>

              <p className="text-secondary-text">
                Our upcoming milestones. Dates are intentionally flexible so we
                can ship the right thing at the right time.
              </p>
            </div>

            <div className="home__roadmap__rows__wrapper flex flex-col lg:gap-8 mt-24">
              <div className="home__roadmap__rows">
                <div className="home__roadmap__row">
                  <div className="home__roadmap__col">
                    Security (KYC / audit / multisig)
                  </div>
                  <div className="home__roadmap__col">Official launch</div>
                  <div className="home__roadmap__col">
                    Second phase of marketing
                  </div>
                </div>
              </div>

              <div className="home__roadmap__rows lg:ml-auto">
                <div className="home__roadmap__row">
                  <div className="home__roadmap__col">Website</div>
                  <div className="home__roadmap__col">
                    Launch Discord and official accounts
                  </div>
                  <div className="home__roadmap__col">More to come</div>
                </div>
              </div>
            </div>

            <p className="text-center mt-16 text-secondary-text">
              Optimize your swaps across hundreds of DEXes on multiple networks.
            </p>
          </div>
        </section>

        <div className="px-4">
          <section
            id="partners"
            className="home__partners__wrapper reveal"
            aria-labelledby="partners-title"
          >
            <div className="home__partners p-10 md:p-16">
              <h2
                id="partners-title"
                className="lg:text-5xl text-3xl uppercase text-center"
              >
                Our partners &amp; stakeholders
              </h2>

              <div className="mt-16 md:mt-24">
                <Partners />
              </div>
            </div>
          </section>
        </div>

        <section className="home__community reveal" aria-labelledby="community-title">
          <div className="container">
            <h2 id="community-title" className="text-2xl md:text-3xl mb-2">
              Join the community
            </h2>
            <p className="text-secondary-text mb-10">
              Follow Aldebaran across the platforms you already use.
            </p>
            <Socials />
          </div>
        </section>
      </div>
    </div>
  );
}

export default Index;
